import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, BookOpen, Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * AnimatedStoryOverlay — fullscreen storybook player.
 * props: { open, onClose, story, loading }
 *   story = { title, slides: [{ page_order, slide_index, title, bullets[], image_url, image_prompt }] }
 *
 * Images are lazy-loaded: when a slide is reached and doesn't have image_url, we
 * call /api/ai/story/slide-image with its image_prompt. Also pre-fetches the next slide.
 */
export default function AnimatedStoryOverlay({ open, onClose, story, loading }) {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [slideImages, setSlideImages] = useState({}); // { slideIndex: image_url }
  const [imageLoading, setImageLoading] = useState({}); // { slideIndex: bool }
  const [progress, setProgress] = useState(null); // { done, total }
  const [promptOpen, setPromptOpen] = useState(false);
  const [imgPrompt, setImgPrompt] = useState("");
  const [promptBusy, setPromptBusy] = useState(false);
  const startedForRef = useRef("");

  useEffect(() => { if (open) { setIdx(0); setSlideImages({}); setImageLoading({}); setProgress(null); setPromptOpen(false); } }, [open, story?.title]);

  // Text-to-speech narration
  useEffect(() => {
    if (!open || muted || !story?.slides?.length) return;
    const sl = story.slides[idx];
    if (!sl) return;
    try {
      const txt = `${sl.title}. ${(sl.bullets || []).join(". ")}.`;
      const u = new SpeechSynthesisUtterance(txt);
      u.rate = 0.95; u.pitch = 1.05;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
      return () => window.speechSynthesis.cancel();
    } catch { /* tts not available */ }
  }, [idx, muted, open, story]);

  useEffect(() => () => { try { window.speechSynthesis.cancel(); } catch { /* no-op */ } }, []);

  // Generate ALL missing illustrations SEQUENTIALLY — slide 1 first (shown the
  // moment it's ready), the rest keep cooking in the background with progress.
  useEffect(() => {
    if (!open || !story?.slides?.length || startedForRef.current === story.id) return;
    startedForRef.current = story.id;
    let stop = false;
    (async () => {
      const slides = story.slides;
      const missing = slides.map((s, i) => ({ s, i })).filter(({ s }) => !s.image_url);
      if (!missing.length) return;
      let done = slides.length - missing.length;
      setProgress({ done, total: slides.length });
      for (const { s, i } of missing) {
        if (stop) return;
        setImageLoading((m) => ({ ...m, [i]: true }));
        try {
          const { data } = await api.post("/ai/story/slide-image", {
            prompt: s.image_prompt || s.title, story_id: story.id, slide_index: s.slide_index,
          });
          if (data.image_url && !stop) setSlideImages((m) => ({ ...m, [i]: data.image_url }));
        } catch { /* placeholder stays */ }
        finally { setImageLoading((m) => ({ ...m, [i]: false })); }
        done += 1;
        if (!stop) setProgress({ done, total: slides.length });
      }
      if (!stop) setProgress(null);
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, story]);

  // Regenerate the CURRENT slide's image from the teacher's own text prompt —
  // for when the AI's default illustration isn't what they wanted.
  const regenFromPrompt = async () => {
    const p = imgPrompt.trim();
    if (!p || !story?.slides?.[idx]) return;
    setPromptBusy(true);
    try {
      const sl = story.slides[idx];
      const { data } = await api.post("/ai/story/slide-image", {
        prompt: p, story_id: story.id, slide_index: sl.slide_index, force: true,
      });
      if (data.image_url) { setSlideImages((m) => ({ ...m, [idx]: data.image_url })); setPromptOpen(false); setImgPrompt(""); }
    } catch { /* toast-free — placeholder stays */ }
    finally { setPromptBusy(false); }
  };

  if (!open) return null;

  const slides = story?.slides || [];
  const total = slides.length;
  const sl = slides[idx];
  const currentImage = slideImages[idx] || sl?.image_url;
  const isImgLoading = imageLoading[idx];
  const pct = progress ? Math.round((progress.done / progress.total) * 100) : 100;

  return (
    <div className="fixed inset-0 z-[60] bg-gradient-to-br from-[#FFF4ED] via-[#FFE8D5] to-[#FFD2B0]" data-testid="story-overlay">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 px-6 lg:px-10 py-5 flex items-center justify-between z-10">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#9A3412] font-bold">/ animated story</div>
          <h1 className="font-display font-bold text-xl sm:text-2xl tracking-tighter text-[#7C2D12] mt-1 max-w-2xl">
            {loading ? "Crafting your story…" : story?.title}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {progress && (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-white/70 backdrop-blur rounded-md" data-testid="story-image-progress">
              <Loader2 size={12} className="animate-spin text-[#FF5C8A]" />
              <span className="text-[11px] font-mono text-[#7C2D12] font-bold">Illustrations {progress.done}/{progress.total} · {pct}%</span>
              <span className="w-16 h-1 rounded bg-[#9A3412]/15 overflow-hidden"><span className="block h-full bg-gradient-to-r from-[#FF8A4C] to-[#FF5C8A] transition-all" style={{ width: `${pct}%` }} /></span>
            </div>
          )}
          <button
            onClick={() => setMuted((m) => !m)}
            data-testid="story-sound"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white/70 backdrop-blur rounded-md text-[#7C2D12] hover:bg-white transition-colors text-xs font-medium"
            aria-label={muted ? "Unmute narration" : "Mute narration"}
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            <span>{muted ? "Sound off" : "Sound on"}</span>
          </button>
          <button
            onClick={onClose}
            data-testid="story-close"
            className="w-10 h-10 rounded-md bg-white/70 hover:bg-white text-[#7C2D12] inline-flex items-center justify-center transition-colors"
            aria-label="Close story"
          ><X size={18} /></button>
        </div>
      </div>

      {/* Body — full-width edge-to-edge layout */}
      <div className="pt-24 pb-24 h-full overflow-y-auto">
        <div className="w-full px-6 lg:px-12">
          {loading ? (
            <LoadingShell />
          ) : total === 0 ? (
            <EmptyShell />
          ) : (
            <>
              {/* Scene counter */}
              <div className="text-center mb-8">
                <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#9A3412] font-bold">
                  Scene {idx + 1} / {total}
                </div>
                <div className="mt-2 h-1 w-40 mx-auto bg-[#9A3412]/15 rounded overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#FF8A4C] to-[#FF5C8A]"
                    initial={{ width: 0 }} animate={{ width: `${((idx + 1) / total) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }}
                  transition={{ duration: 0.35 }}
                  className="grid lg:grid-cols-[55fr_45fr] gap-0 items-stretch w-full max-w-[1600px] mx-auto min-h-[60vh]"
                >
                  {/* Image — full half edge to edge */}
                  <div className="relative overflow-hidden bg-[#FFE8D5] min-h-[60vh] lg:rounded-l-2xl rounded-2xl lg:rounded-r-none shadow-[0_24px_80px_-30px_rgba(124,45,18,0.5)]">
                    {currentImage ? (
                      <img src={currentImage} alt={sl.title} className="w-full h-full object-cover" />
                    ) : isImgLoading ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[#9A3412]">
                        <Loader2 size={48} className="mb-3 animate-spin text-[#FF5C8A]" />
                        <div className="text-xs font-mono uppercase tracking-wider">Drawing scene…</div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[#9A3412]/60">
                        <BookOpen size={48} className="mb-3" />
                        <div className="text-xs font-mono uppercase tracking-wider">No image</div>
                      </div>
                    )}
                    <div className="absolute top-4 left-4 px-2.5 py-1 bg-[#FF5C8A] text-white text-[10px] font-mono font-bold rounded">
                      P{sl?.page_order} · S{sl?.slide_index}
                    </div>
                    <button
                      onClick={() => { setPromptOpen((v) => !v); setImgPrompt(""); }}
                      data-testid="story-image-prompt-btn"
                      className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/85 backdrop-blur text-[#7C2D12] text-xs font-bold hover:bg-white transition-colors shadow"
                    ><Sparkles size={12} className="text-[#FF5C8A]" /> New image from prompt</button>
                    {promptOpen && (
                      <div className="absolute bottom-14 right-4 left-4 flex items-center gap-2 bg-white rounded-md p-2 shadow-xl" data-testid="story-image-prompt-bar">
                        <input
                          autoFocus
                          value={imgPrompt}
                          onChange={(e) => setImgPrompt(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && !promptBusy && regenFromPrompt()}
                          placeholder="Describe the image you want for this slide…"
                          data-testid="story-image-prompt-input"
                          className="flex-1 px-3 py-2 rounded-md border border-neutral-200 text-sm text-[#7C2D12] outline-none focus:border-[#FF5C8A]"
                        />
                        <button
                          onClick={regenFromPrompt} disabled={promptBusy || !imgPrompt.trim()}
                          data-testid="story-image-prompt-go"
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-gradient-to-r from-[#FF8A4C] to-[#FF5C8A] text-white text-sm font-bold disabled:opacity-40"
                        >{promptBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate</button>
                      </div>
                    )}
                  </div>

                  {/* Text — half panel, edge-flush */}
                  <div className="bg-white/85 backdrop-blur-sm px-8 lg:px-14 py-10 lg:py-16 lg:rounded-r-2xl rounded-2xl lg:rounded-l-none flex flex-col justify-center">
                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FF5C8A] font-bold">Slide {idx + 1}</div>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#7C2D12] leading-[1.05] mt-3">
                      {sl?.title}
                    </h2>
                    <ul className="mt-8 space-y-4">
                      {(sl?.bullets || []).map((b, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.12 + i * 0.08 }}
                          className="flex items-start gap-3 text-[#7C2D12]"
                        >
                          <Sparkles size={16} className="text-[#FF5C8A] mt-1.5 shrink-0" />
                          <span className="text-lg leading-relaxed">{b}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </AnimatePresence>
            </>
          )}
        </div>
      </div>

      {/* Bottom nav */}
      {total > 0 && !loading && (
        <div className="absolute bottom-0 inset-x-0 px-6 lg:px-10 py-5 flex items-center justify-between bg-gradient-to-t from-white/80 to-transparent backdrop-blur-sm">
          <button
            onClick={() => idx > 0 && setIdx(idx - 1)}
            disabled={idx === 0}
            data-testid="story-prev"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white text-[#7C2D12] hover:bg-[#FFE8D5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          ><ChevronLeft size={14} /> Previous</button>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#9A3412]/70">
            Tap → to continue
          </div>
          <button
            onClick={() => idx < total - 1 ? setIdx(idx + 1) : onClose()}
            data-testid="story-next"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gradient-to-r from-[#FF8A4C] to-[#FF5C8A] text-white text-sm font-semibold hover:scale-[1.02] transition-transform"
          >
            {idx < total - 1 ? <>Next <ChevronRight size={14} /></> : <>The end ✨</>}
          </button>
        </div>
      )}
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="text-center py-28">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/70 backdrop-blur mb-5">
        <Loader2 size={26} className="text-[#FF5C8A] animate-spin" />
      </div>
      <div className="font-display font-bold text-3xl text-[#7C2D12] tracking-tighter">Weaving your story…</div>
      <p className="text-[#9A3412]/70 mt-2 text-sm">Reading every page, drawing illustrations, picking the perfect words.</p>
      <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[#9A3412]/50">
        <Sparkles size={12} /> This takes ~30 seconds per page.
      </div>
    </div>
  );
}

function EmptyShell() {
  return (
    <div className="text-center py-28">
      <BookOpen size={36} className="mx-auto text-[#9A3412]/50" />
      <div className="font-display font-bold text-2xl text-[#7C2D12] tracking-tighter mt-3">No slides yet</div>
      <p className="text-[#9A3412]/70 mt-2 text-sm">Scan a couple of pages first so the AI has something to work with.</p>
    </div>
  );
}
